<template>
  <div class="relative w-48" v-click-outside="closeOptions">
    <div
      class="actionButton"
      :class="validationStatus.$error && `error-mode`"
      @click="toggleOptions"
    >
      <div>
        {{
          selectedItems.length > 0
            ? selectedItems.length > 1
              ? `${selectedItems[0].label}, ...`
              : selectedItems[0].label
            : optionLabel
        }}
      </div>
      <font-awesome-icon
        icon="chevron-down"
        :class="isOptionsWrapperToggled ? `transform rotate-180 ease-in` : ``"
      />
    </div>

    <div v-if="isOptionsWrapperToggled" class="optionWrapper">
      <div
        v-for="(option, index) in options"
        class="flex items-center justify-center py-2"
        :key="`option-${index}-${option.key}`"
        @click="selectOption(option)"
      >
        <base-input
          :id="option.key"
          :name="option.key"
          type="checkbox"
          :checked="isOptionSelected(option)"
        />
        <span class="whitespace-nowrap">{{ option.label }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, SetupContext } from "vue";
import { BaseInput } from "components/atoms";

type OptionType = {
  key: string;
  label: string;
};

interface DropdownProps {
  options: OptionType[];
  placeholder?: string;
  modelValue: string[];
  optionLabel?: string;
  optionValue?: string;
  validationStatus: object;
}

export default {
  name: "MultiDropdown",
  components: { BaseInput },
  props: {
    value: {
      required: true,
      type: Array as () => string[],
    },
    options: {
      required: true,
      type: Array as () => OptionType[],
    },
    placeholder: {
      required: false,
      type: String,
      default: "Click here to select",
    },
    modelValue: {
      type: Array as () => string[],
      required: true,
    },
    optionLabel: {
      required: false,
      type: String,
      default: "label",
    },
    optionValue: {
      required: false,
      type: String,
      default: "value",
    },
    validationStatus: {
      type: Object,
      required: true,
    },
  },
  setup(props: DropdownProps, { emit }: SetupContext) {
    const isOptionsWrapperToggled = ref(false);
    const selectedItems = ref([] as OptionType[]);

    /**
     * Toggle/Untoggle the main option wrapper
     */
    const toggleOptions = () => {
      isOptionsWrapperToggled.value = !isOptionsWrapperToggled.value;
    };

    /**
     * Close the option dialog on outside click
     */
    const closeOptions = () => (isOptionsWrapperToggled.value = false);

    /**
     * Return the option index in the selected list
     */
    const getOptionIndex = (option: OptionType) => {
      return selectedItems.value.findIndex(
        (selectedOption: OptionType) => selectedOption.key === option.key
      );
    };

    /**
     * Return the state of a specific option (boolean)
     */
    const isOptionSelected = (option: OptionType) => {
      return getOptionIndex(option) !== -1;
    };

    /**
     * Handle the selection/unselection action on the selectedItems array
     */
    const selectOption = (option: OptionType) => {
      const index = getOptionIndex(option);
      if (index === -1) {
        // Push new entry to the selection array
        selectedItems.value.push(option);
      } else {
        // Remove item from selected array
        selectedItems.value.splice(index, 1);
      }

      // Emit update event
      emit("update:modelValue", selectedItems.value);
    };

    return {
      isOptionsWrapperToggled,
      isOptionSelected,
      toggleOptions,
      selectOption,
      emit,
      selectedItems,
      closeOptions,
    };
  },
  emits: ["update:modelValue"],
};
</script>

<style scoped>
.actionButton {
  @apply flex;
  @apply items-center;
  @apply justify-between;
  @apply w-full;
  @apply py-2;
  @apply px-4;
  @apply text-sm;
  @apply text-gray-700;
  @apply rounded;
  @apply cursor-pointer;
  @apply border;
  @apply border-gray-400;
  transition: duration-75 ease-in;
}

.error-mode {
  @apply border-red-500;
  @apply text-red-500;
}

.actionButton:focus {
  @apply outline-none;
  @apply bg-gray-200;
}

.actionButton:hover {
  @apply bg-gray-200;
}

.optionWrapper {
  @apply absolute;
  @apply w-40;
  @apply top-full;
  @apply flex;
  @apply flex-col;
  @apply items-start;
  @apply py-2;
  @apply bg-white;
  @apply shadow-md;
  @apply rounded;
  @apply z-10;
}
</style>
